/**
 * Global Functionality
 * The logic in this is intended to ONLY contain any functionality
 * that is required on every page load.
 *
 * NOTE: Please do not include any page-specific logic here.
 **/

import Logger from "../lib/logger";
import SmoothScroll from "../lib/utils/SmoothScroll";
import ThemeContentModal from "../lib/partials/ThemeContentModal";
import ThemeFloatingContentBanner from "../lib/partials/ThemeFloatingContentBanner";
import ThemeHeader from "../lib/partials/ThemeHeader";
import ThemeHeroBanner from "../lib/partials/ThemeHeroBanner";
import ThemeToast from "../lib/partials/ThemeToast";
import ThemeUtilityBar from "../lib/partials/ThemeUtilityBar";

export default function () {
	const NAME = 'Global';
	Logger.info(`${NAME} > loading`);

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	let smoothScroll = null;


	// Logic
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * init --- Initialization block
	 */
	function init() {
		Logger.info(`${NAME} > init`);
		// initSmoothScroll();
		initThemeContentModal();
		initThemeFloatingContentBanner();
		initThemeHeader();
		initThemeHeroBanner();
		initStopBodyScrolling();
		initThemeUtilityBar();
		initThemeToast();
		initImageReveal();
	}

	/**
	 * initSmoothScroll - Initialize premium smooth scrolling
	 */
	function initSmoothScroll() {
		Logger.info(`${NAME} > initSmoothScroll`);
		smoothScroll = new SmoothScroll();
		smoothScroll.init();
	}

	/**
	 * initThemeContentModal
	 */
	function initThemeContentModal() {
		Logger.info(`${NAME} > initThemeContentModal`);
		const themeContentModalDom = document.querySelector('[data-theme-content-modal]');
		if (!themeContentModalDom) return;
		const themeContentModal = new ThemeContentModal(themeContentModalDom);
		themeContentModal.init();
	}

	/**
	 * initThemeFloatingContentBanner
	 */
	function initThemeFloatingContentBanner() {
		Logger.info(`${NAME} > initThemeFloatingContentBanner`);
		const themeFloatingContentBannerDoms = document.querySelectorAll('.theme-floating-content-banner');
		if (!themeFloatingContentBannerDoms.length) return;
		themeFloatingContentBannerDoms.forEach((dom) => {
			const themeFloatingContentBanner = new ThemeFloatingContentBanner(dom);
			themeFloatingContentBanner.init();
		});
	}

	/**
	 * initThemeHeroBanner
	 */
	function initThemeHeroBanner() {
		Logger.info(`${NAME} > initThemeHeroBanner`);
		const themeHeroBannerDoms = document.querySelectorAll('.theme-hero-banner');
		if (!themeHeroBannerDoms.length) return;
		themeHeroBannerDoms.forEach((dom) => {
			const themeHeroBanner = new ThemeHeroBanner(dom);
			themeHeroBanner.init();
		});
	}

	/**
	 * initThemeHeader
	 */
	function initThemeHeader() {
		Logger.info(`${NAME} > initThemeHeader`);

		const themeHeaderDom = document.querySelector('[data-theme-header]');
		if (!themeHeaderDom) return;
		const themeHeader = new ThemeHeader(themeHeaderDom);
		themeHeader.init();
	}

	/**
	 * initStopBodyScrolling
	 */
	function initStopBodyScrolling() {
		Logger.info(`${NAME} > initStopBodyScrolling`);

		// Listen for window event
		window.addEventListener('body-scroll:start', () => {
			stopBodyScrolling(true);
		});

		window.addEventListener('body-scroll:stop', () => {
			stopBodyScrolling(false);
		});
	}

	/**
	 * initThemeUtilityBar
	 */
	function initThemeUtilityBar() {
		Logger.info(`${NAME} > initThemeUtilityBar`);
		const themeUtilityBarDom = document.querySelector('[data-theme-utility-bar]');
		if (!themeUtilityBarDom) return;
		const themeUtilityBar = new ThemeUtilityBar(themeUtilityBarDom);
		themeUtilityBar.init();
	}

	/**
	 * initThemeToast
	 */
	function initThemeToast() {
		Logger.info(`${NAME} > initThemeToast`);
		const themeToastDom = document.querySelector('[data-theme-toast]');
		if (!themeToastDom) return;
		const themeToast = new ThemeToast(themeToastDom);
		themeToast.init();
	}

	/**
	 * initImageReveal - Handle image reveal animations on load
	 * Opt out per image: data-image-reveal="false" (you manage .loaded / data-loaded yourself).
	 */
	function initImageReveal() {
		Logger.info(`${NAME} > initImageReveal`);

		function applyImageReveal(img) {
			if (img.getAttribute('data-image-reveal') === 'false') {
				return;
			}
			if (img.complete && img.naturalHeight !== 0) {
				img.classList.add('loaded');
				img.setAttribute('data-loaded', 'true');
			} else if (img.loading === 'lazy') {
				img.addEventListener('load', () => {
					img.classList.add('loaded');
					img.setAttribute('data-loaded', 'true');
				}, { once: true });

				img.addEventListener('error', () => {
					// Even on error, show the image (or placeholder)
					img.classList.add('loaded');
					img.setAttribute('data-loaded', 'true');
				}, { once: true });
			} else {
				img.addEventListener('load', () => {
					img.classList.add('loaded');
					img.setAttribute('data-loaded', 'true');
				}, { once: true });
			}
		}

		document.querySelectorAll('img').forEach(applyImageReveal);

		// Handle dynamically added images (e.g., from AJAX)
		if (window.MutationObserver) {
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					mutation.addedNodes.forEach((node) => {
						if (node.nodeType !== 1) return;
						const newImages = [];
						if (node.matches && node.matches('img')) {
							newImages.push(node);
						}
						if (node.querySelectorAll) {
							node.querySelectorAll('img').forEach((img) => newImages.push(img));
						}
						newImages.forEach(applyImageReveal);
					});
				});
			});

			observer.observe(document.body, {
				childList: true,
				subtree: true
			});
		}
	}

	/**
	 * Stop the body from scrolling
	 */
	function stopBodyScrolling(bool) {
		Logger.info(`${NAME} > stopBodyScrolling`);
		if (bool === true) {
			document.body.style.overflow = 'hidden'; document.getElementsByTagName('html')[0].style.overflow = `hidden`;
		} else {
			document.body.style.overflow = 'auto'; document.getElementsByTagName('html')[0].style.overflow = `auto`;
		}
	}

	// Do not edit below this line...
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
	Logger.info(`${NAME} > loaded`);

	init();
}
